import { BaseInputFilter } from "domain/models/base-input-filter";
import { useJobTitleByFilter } from "presentation/hooks/use-query-job-title";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import {
  useCreateProjectMutation,
  useProjectsById,
  useUpdateProjectMutation,
} from "presentation/hooks/use-query-project";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { useProjectStore } from "presentation/stores/use-project-store";
import { Button } from "primereact/button";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TeamInfoGrid from "./TeamInfoGrid";
import "./index.css";
import ProjectInfoForm from "./ProjectInfoForm";
import { useClientByFilter } from "presentation/hooks/use-query-client";
import { useClientStore } from "presentation/stores/use-client-store";
import { Parameter } from "domain/entities/parameter";
import { useProjectMemberUploadStore } from "presentation/stores/use-project-member-upload-store";
import { useProjectMemberDeleteStore } from "presentation/stores/use-project-member-delete-store";
import { ProjectMember } from "domain/entities/project-member";
import { Project } from "domain/entities/project";
import Spinner from "presentation/components/Shared/Spinner";

const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { id } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);
  const [buttonLabel, setButtonLabel] = useState("Próximo");
  const { project, setProject, resetProject } = useProjectStore();
  const { setProjectStatus, setProjectRole, setProjectMemberStatus } =
    useParameterStore();
  const { client, setClient } = useClientStore();
  const { projectMembersUpload, setProjectMembersUpload } =
    useProjectMemberUploadStore();
  const { projectMembersDelete, setProjectMembersDelete } =
    useProjectMemberDeleteStore();
  const {
    mutateAsync: createProjectMutation,
    error: creationError,
    isError: isCreationError,
    isSuccess: isCreationSuccess,
    data: projectCreated,
    isPending: isCreationPending,
  } = useCreateProjectMutation();
  const {
    mutateAsync: updateProjectMutation,
    error: updatingError,
    isError: isUpdatingError,
    isSuccess: isUpdatingSuccess,
    isPending: isUpdatingPending,
  } = useUpdateProjectMutation();
  const { data, isError, isLoading, error } = useProjectsById(id);

  const { data: parameters } = useParameterByFilter({
    limit: 1000,
    sortBy: "value",
  } as BaseInputFilter);
  const { data: jobTitles } = useJobTitleByFilter({
    limit: 100,
  } as BaseInputFilter);

  const { data: clients } = useClientByFilter({
    limit: 100,
  } as BaseInputFilter);

  // const projectMembers = useProjectMembersByFilter({
  //   projectId: project?.id,
  // } as ProjectMemberInputFilter);

  const normalizeToMidnight = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // #region Data Loading Effects
  /**
   * Carrega e formata a lista de clientes para o dropdown.
   */
  useEffect(() => {
    const formatedClients = clients?.data?.map((item) => {
      const employee: Parameter = {
        id: item.id,
        value: item.name,
        type: "client",
      };

      return employee;
    });
    setClient(formatedClients);
  }, [clients]);

  /**
   * Carrega os parâmetros do sistema (status, funções, etc.) e atualiza as stores.
   */
  useEffect(() => {
    setProjectStatus(
      parameters?.data?.filter((item) => item.type === "projectStatus")
    );

    setProjectRole(
      parameters?.data?.filter((item) => item.type === "projectRole")
    );

    setProjectMemberStatus(
      parameters?.data?.filter((item) => item.type === "projectMemberStatus")
    );
  }, [parameters]);

  /**
   * Atualiza o estado do projeto quando os dados são carregados da API (modo edição).
   */
  useEffect(() => {
    if (data && data.data) {
      setProject(data.data);
    } else if (isError) {
      console.error("Erro ao carregar dados do colaborador:", error);
    }
  }, [data, isError, error]);


  /**
   * Função principal de submissão do formulário.
   * - Calcula o status do projeto.
   * - Gere a lógica de projetos "Concluídos" (inativando membros).
   * - Orquestra as chamadas de atualização de membros (pré e pós) e do projeto.
   */
  const handleSave = async () => {
    if (project) {
      const currentDate = new Date();
      const projectDates = project.startAt &&
        project.endAt && {
        startAt: new Date(project.startAt),
        endAt: new Date(project.endAt),
      };
      const members = getProjectMembers(project);
      if (members && members.length > 0) {
        project.projectMembers = members;
      }
      const projectStatus =
        projectDates && currentDate < projectDates.startAt
          ? "Previsto"
          : projectDates && currentDate > projectDates.endAt
            ? "Concluído"
            : "Em progresso";
      project.status = projectStatus;

      try {
        const saveProjectAction = project.id
          ? updateProjectMutation
          : createProjectMutation;
        await saveProjectAction(project);
      } catch (error) {
        console.error("Erro ao salvar o projeto:", error);
      }
      return;
    }
  };

  const getProjectMembers = (project: Project) => {
    const members: ProjectMember[] = [];
    projectMembersUpload.forEach((item) => {
      if (item.employee) {
        const currentProjectMember: ProjectMember = {
          id: item.id,
          startAt: item.startAt,
          endAt: item.endAt,
          status: item.status,
          employee: {
            id: item.employee.id,
            name: item.employee.name,
          },
          project: {
            id: project.id,
            name: project.name,
          } as Project,
          projectRole: item.projectRole,
        };

        members.push(currentProjectMember);
      }
    });
    return members;
  };
  /**
   * Gere a navegação entre as abas do formulário.
   * Se estiver na última aba, aciona o `handleSave`.
   */
  const handleTabNavigation = () => {
    if (activeIndex < 1) {
      setActiveIndex(activeIndex + 1);
    } else {
      handleSave();
    }
  };

  // #endregion

  // #region Feedback & Navigation Effects
  useEffect(() => {
    if (isCreationError || isUpdatingError) {
      console.log(updatingError);
      let message = [];
      message.push(
        isCreationError ? creationError.message : updatingError.message
      );

      if (
        creationError &&
        creationError.response?.data.metadata.errors &&
        creationError.response?.data.metadata.errors.length > 0
      ) {
        message = creationError.response.data.metadata.errors;
      } else if (
        updatingError &&
        updatingError.response?.data.metadata.errors &&
        updatingError.response?.data.metadata.errors.length > 0
      ) {
        message = updatingError.response.data.metadata.errors;
      }
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [creationError, updatingError]);


  useEffect(() => {
    if (isCreationSuccess || isUpdatingSuccess) {
      const projectId = project?.id || projectCreated?.data?.id;

      if (!projectId) {
        console.error("Erro: projectId está undefined!");
        return;
      }

      const executeSave = async () => {
        // await saveProjectMembers(projectId);

        const successMessage = isCreationSuccess
          ? "Projeto registrado com sucesso"
          : "Projeto atualizado com sucesso";

        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: successMessage,
          life: 1500,
        });

        setTimeout(() => {
          resetProject();
          navigate("/projects");
        }, 2000);
      };
      executeSave();
    }
  }, [
    isCreationSuccess,
    isUpdatingSuccess,
    resetProject,
    navigate,
    projectCreated,
  ]);
  useEffect(() => {
    activeIndex === 1 ? setButtonLabel("Guardar") : setButtonLabel("Próximo");
  }, [activeIndex]);

  if (isUpdatingPending || isCreationPending) {
    return <Spinner />;
  }

  return (
    <div className="card">
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        renderActiveOnly={false}
      >
        <TabPanel header="Projeto" leftIcon="pi pi-sitemap mr-2">
          <ProjectInfoForm />
        </TabPanel>
        <TabPanel header="Equipa" leftIcon="pi pi-users mr-2">
          <TeamInfoGrid />
        </TabPanel>
      </TabView>
      <div className="next-button">
        {" "}
        <Button
          label={buttonLabel}
          onClick={handleTabNavigation}
          className="button-size"
        />
      </div>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};
export default ProjectForm;
